import React, { useState, useMemo } from "react";
import { Modal, message, InputNumber, Input, Switch } from "antd";
import {
  CloseCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  WarningOutlined,
  CalendarOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type {
  SupplierCustomValue,
  DailyRevenueSupplierExpense,
} from "../../../utils/api/supplierExpensesAPI";
import {
  updateCustomValuePaymentStatus,
  updateDailyRevenueExpensePaymentStatus,
} from "../../../utils/api/supplierExpensesAPI";
import dayjs from "dayjs";
import "./UnpaidExpensesModal.scss";

interface UnpaidExpensesModalProps {
  visible: boolean;
  onClose: () => void;
  customValues: SupplierCustomValue[];
  dailyRevenueExpenses: DailyRevenueSupplierExpense[];
  onRefresh: () => Promise<void>;
  locationFilters?: string[];
  selectedMonths?: string[];
  filterMode?: "in-time" | "overdue";
}

interface TableRow {
  key: string;
  type: "custom" | "daily";
  expenseSubtype: "fixed" | "custom" | "daily";
  id: string;
  supplier: string;
  description: string;
  date: string;
  dueDate?: string;
  amount: number;
  currency: string;
  paymentStatus: "unpaid" | "partial";
  paidAmount?: number;
  remainingAmount?: number;
  originalData: SupplierCustomValue | DailyRevenueSupplierExpense;
}

export const UnpaidExpensesModal: React.FC<UnpaidExpensesModalProps> = ({
  visible,
  onClose,
  customValues,
  dailyRevenueExpenses,
  onRefresh,
  locationFilters = [],
  selectedMonths = [],
  filterMode,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [partialPaymentModalVisible, setPartialPaymentModalVisible] =
    useState(false);
  const [selectedRow, setSelectedRow] = useState<TableRow | null>(null);
  const [partialAmount, setPartialAmount] = useState<number>(0);
  const [showNonCustom, setShowNonCustom] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const today = dayjs().startOf("day");

  const isOverdue = (dueDate: string | undefined) =>
    !!dueDate && dayjs(dueDate).startOf("day").isBefore(today);

  const modalTitle = useMemo(() => {
    if (filterMode === "in-time") return t("Unpaid within deadline");
    if (filterMode === "overdue") return t("Unpaid overdue");
    return t("Unpaid and partially unpaid expenses");
  }, [filterMode, t]);

  // Build all unpaid rows (unfiltered by mode / showNonCustom / search)
  const allRows: TableRow[] = useMemo(() => {
    const rows: TableRow[] = [];

    const filterByLocation = (locationId: string | undefined) => {
      if (locationFilters.length === 0) return true;
      if (!locationId) return false;
      return locationFilters.includes(locationId);
    };

    const filterByMonth = (month: number, year: number) => {
      if (selectedMonths.length === 0) return true;
      const itemMonth = `${year}-${String(month).padStart(2, "0")}`;
      return selectedMonths.includes(itemMonth);
    };

    customValues
      .filter((cv) => cv.paymentStatus === "unpaid" || cv.paymentStatus === "partial")
      .filter((cv) => filterByLocation(cv.locationId))
      .filter((cv) => filterByMonth(cv.month, cv.year))
      .forEach((cv) => {
        const isFixed = cv.type === "fixed";
        rows.push({
          key: `custom-${cv._id}`,
          type: "custom",
          expenseSubtype: isFixed ? "fixed" : "custom",
          id: cv._id,
          supplier: cv.supplierName,
          description: cv.description || (isFixed ? t("Fixed expense") : t("Custom expense")),
          date: cv.invoiceDate || "",
          dueDate: cv.dueDate || undefined,
          amount: cv.amount,
          currency: cv.currency,
          paymentStatus: cv.paymentStatus as "unpaid" | "partial",
          paidAmount: cv.partialPayment?.paidAmount,
          remainingAmount: cv.partialPayment?.remainingAmount,
          originalData: cv,
        });
      });

    dailyRevenueExpenses
      .filter((exp) => exp.paymentStatus === "unpaid" || exp.paymentStatus === "partial")
      .filter((exp) => filterByLocation(exp.locationId))
      .filter((exp) => {
        if (selectedMonths.length === 0) return true;
        if (!exp.date) return false;
        const expMonth = dayjs(exp.date).format("YYYY-MM");
        return selectedMonths.includes(expMonth);
      })
      .forEach((exp) => {
        rows.push({
          key: `daily-${exp._id}`,
          type: "daily",
          expenseSubtype: "daily",
          id: exp._id,
          supplier: exp.supplierName,
          description: exp.description || t("Daily expense"),
          date: exp.date || "",
          amount: exp.amount,
          currency: "RSD",
          paymentStatus: exp.paymentStatus as "unpaid" | "partial",
          paidAmount: exp.partialPayment?.paidAmount,
          remainingAmount: exp.partialPayment?.remainingAmount,
          originalData: exp,
        });
      });

    return rows;
  }, [customValues, dailyRevenueExpenses, locationFilters, selectedMonths, t]);

  // Apply filterMode and showNonCustom toggle
  const tableData: TableRow[] = useMemo(() => {
    let rows = allRows;

    if (filterMode === "in-time") {
      rows = rows.filter((r) => !isOverdue(r.dueDate));
    } else if (filterMode === "overdue") {
      rows = rows.filter((r) => isOverdue(r.dueDate));
    }

    if (!showNonCustom) {
      rows = rows.filter((r) => r.expenseSubtype === "custom");
    }

    return rows;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, filterMode, showNonCustom]);

  // Count of non-custom rows (to show in toggle label)
  const nonCustomCount = useMemo(() => {
    let rows = allRows;
    if (filterMode === "in-time") rows = rows.filter((r) => !isOverdue(r.dueDate));
    else if (filterMode === "overdue") rows = rows.filter((r) => isOverdue(r.dueDate));
    return rows.filter((r) => r.expenseSubtype !== "custom").length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, filterMode]);

  const getDueDateInfo = (dueDate: string | undefined) => {
    if (!dueDate) return null;
    const due = dayjs(dueDate).startOf("day");
    const diff = due.diff(today, "day");
    if (diff < 0) return { diff, label: t("Overdue {{count}} days", { count: Math.abs(diff) }), icon: "overdue" as const };
    if (diff === 0) return { diff, label: t("Due today"), icon: "today" as const };
    if (diff <= 7) return { diff, label: t("Due in {{count}} days", { count: diff }), icon: "soon" as const };
    return { diff, label: t("Due in {{count}} days", { count: diff }), icon: "ok" as const };
  };

  const sortedRows = useMemo(
    () =>
      [...tableData].sort((a, b) => {
        const aInfo = getDueDateInfo(a.dueDate);
        const bInfo = getDueDateInfo(b.dueDate);
        if (aInfo && bInfo) return aInfo.diff - bInfo.diff;
        if (aInfo) return -1;
        if (bInfo) return 1;
        return 0;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableData],
  );

  const visibleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sortedRows;
    return sortedRows.filter(
      (r) =>
        r.supplier.toLowerCase().includes(term) ||
        r.description.toLowerCase().includes(term),
    );
  }, [sortedRows, searchTerm]);

  const handleOpenPartialPayment = (row: TableRow) => {
    setSelectedRow(row);
    const currentRemaining = row.remainingAmount || row.amount;
    setPartialAmount(currentRemaining);
    setPartialPaymentModalVisible(true);
  };

  const handlePartialPaymentSubmit = async () => {
    if (!selectedRow) return;

    const currentRemaining = selectedRow.remainingAmount || selectedRow.amount;

    if (partialAmount <= 0 || partialAmount > currentRemaining) {
      message.error(t("Paid amount must be greater than 0 and not exceed remaining amount"));
      return;
    }

    const newStatus = partialAmount >= currentRemaining ? "paid" : "partial";
    const totalPaidAmount = (selectedRow.paidAmount || 0) + partialAmount;

    try {
      setLoading(true);
      setProcessingIds((prev) => new Set(prev).add(selectedRow.id));

      if (selectedRow.type === "custom") {
        await updateCustomValuePaymentStatus(
          selectedRow.id,
          newStatus,
          newStatus === "partial" ? totalPaidAmount : undefined,
        );
      } else {
        await updateDailyRevenueExpensePaymentStatus(
          selectedRow.id,
          newStatus,
          newStatus === "partial" ? totalPaidAmount : undefined,
        );
      }

      message.success(t("Payment status updated successfully"));
      setPartialPaymentModalVisible(false);
      setSelectedRow(null);
      await onRefresh();
    } catch (error) {
      console.error("Error updating payment status:", error);
      message.error(t("Failed to update payment status"));
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(selectedRow.id);
        return next;
      });
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (row: TableRow) => {
    try {
      setProcessingIds((prev) => new Set(prev).add(row.id));
      setLoading(true);

      if (row.type === "custom") {
        await updateCustomValuePaymentStatus(row.id, "paid");
      } else {
        await updateDailyRevenueExpensePaymentStatus(row.id, "paid");
      }

      message.success(t("Payment status updated successfully"));
      await onRefresh();
    } catch (error) {
      console.error("Error updating payment status:", error);
      message.error(t("Failed to update payment status"));
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      setLoading(false);
    }
  };

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat("sr-RS", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(amount);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return dayjs(dateString).format("DD.MM.YYYY.");
  };

  const totalUnpaid = useMemo(() => {
    const totals = { rsd: 0, eur: 0 };
    tableData.forEach((row) => {
      const currency = row.currency.toLowerCase() as "rsd" | "eur";
      if (row.paymentStatus === "unpaid") {
        totals[currency] += row.amount;
      } else if (row.paymentStatus === "partial") {
        totals[currency] += row.remainingAmount || 0;
      }
    });
    return totals;
  }, [tableData]);

  const typeLabel = (subtype: "fixed" | "custom" | "daily") => {
    if (subtype === "fixed") return t("Fixed");
    if (subtype === "daily") return t("Daily");
    return t("Custom");
  };

  return (
    <>
      <Modal
        title={
          <div className="um-head">
            <div
              className={`um-head-icon ${
                filterMode === "overdue" ? "overdue" : filterMode === "in-time" ? "in-time" : "default"
              }`}
            >
              {filterMode === "overdue" ? <WarningOutlined /> : <CloseCircleOutlined />}
            </div>
            <div>
              <div className="um-head-title">{modalTitle}</div>
              <div className="um-head-sub">
                {t("{{count}} items", { count: sortedRows.length })} ·{" "}
                {formatAmount(totalUnpaid.rsd)} RSD
                {totalUnpaid.eur > 0 && ` | ${formatAmount(totalUnpaid.eur)} EUR`}
              </div>
            </div>
          </div>
        }
        open={visible}
        onCancel={onClose}
        width={880}
        wrapClassName="unpaid-modal"
        footer={
          <div className="um-footer">
            <div className="um-footer-total">
              {t("Total remaining")}
              <span className="um-footer-total-amount">
                {formatAmount(totalUnpaid.rsd)} RSD
                {totalUnpaid.eur > 0 && ` | ${formatAmount(totalUnpaid.eur)} EUR`}
              </span>
            </div>
            {nonCustomCount > 0 && (
              <div className="um-footer-toggle">
                <Switch size="small" checked={showNonCustom} onChange={setShowNonCustom} />
                {t("Show fixed & daily")} ({nonCustomCount})
              </div>
            )}
            <div className="um-footer-actions">
              <button className="um-btn-close" onClick={onClose}>
                {t("Close")}
              </button>
            </div>
          </div>
        }
      >
        <div className="um-toolbar">
          <Input
            className="um-search"
            placeholder={t("Search supplier or description")}
            prefix={<SearchOutlined style={{ color: "var(--um-subtle)" }} />}
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <span className="um-count">
              {t("Showing {{count}} of {{total}}", {
                count: visibleRows.length,
                total: sortedRows.length,
              })}
            </span>
          )}
        </div>

        <div className="um-list">
          <div className="um-list-scroll">
            {visibleRows.length === 0 ? (
              <div className="um-empty">{t("No items")}</div>
            ) : (
              visibleRows.map((row) => {
                const dueDateInfo = getDueDateInfo(row.dueDate);
                return (
                  <div key={row.key} className="um-row">
                    <span className={`um-row-type ${row.expenseSubtype}`} />

                    <div className="um-row-main">
                      <div className="um-row-top">
                        <span className="um-row-supplier">{row.supplier}</span>
                        <span className={`um-row-tag ${row.expenseSubtype}`}>
                          {typeLabel(row.expenseSubtype)}
                        </span>
                      </div>
                      <div className="um-row-desc">{row.description}</div>
                    </div>

                    <div className="um-row-date">
                      <span className="um-row-date-val">{formatDate(row.date)}</span>
                      {dueDateInfo && (
                        <span className={`um-row-due ${dueDateInfo.icon}`}>
                          {dueDateInfo.icon === "overdue" ? (
                            <WarningOutlined style={{ fontSize: 10 }} />
                          ) : (
                            <CalendarOutlined style={{ fontSize: 10 }} />
                          )}
                          {dueDateInfo.label}
                        </span>
                      )}
                    </div>

                    <div className="um-row-amount">
                      <div className="um-row-amount-main">
                        {formatAmount(row.amount)} <span className="cur">{row.currency}</span>
                      </div>
                      {row.paymentStatus === "partial" && (
                        <>
                          <div className="um-row-amount-sub paid">
                            {t("Paid")}: {formatAmount(row.paidAmount || 0)}
                          </div>
                          <div className="um-row-amount-sub remaining">
                            {t("Remaining")}: {formatAmount(row.remainingAmount || 0)}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="um-row-status">
                      <span className={`um-status-pill ${row.paymentStatus}`}>
                        {row.paymentStatus === "unpaid" ? (
                          <CloseCircleOutlined style={{ fontSize: 10 }} />
                        ) : (
                          <ClockCircleOutlined style={{ fontSize: 10 }} />
                        )}
                        {row.paymentStatus === "unpaid" ? t("Unpaid") : t("Partially paid")}
                      </span>
                    </div>

                    <div className="um-row-actions">
                      <button
                        className="um-btn-paid"
                        title={t("Paid")}
                        onClick={() => handleMarkAsPaid(row)}
                        disabled={processingIds.has(row.id)}
                      >
                        <CheckCircleOutlined />
                      </button>
                      <button
                        title={t("Partial")}
                        onClick={() => handleOpenPartialPayment(row)}
                        disabled={processingIds.has(row.id)}
                      >
                        <DollarOutlined />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Modal>

      {/* Partial Payment Modal */}
      <Modal
        title={t("Enter paid amount")}
        open={partialPaymentModalVisible}
        onOk={handlePartialPaymentSubmit}
        onCancel={() => {
          setPartialPaymentModalVisible(false);
          setSelectedRow(null);
        }}
        okText={t("Confirm")}
        cancelText={t("Cancel")}
        className="standardHeader standardFooter"
        confirmLoading={loading}
      >
        {selectedRow && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>{selectedRow.supplier}</strong> - {selectedRow.description}
            </div>
            <div style={{ marginBottom: 8 }}>
              {t("Total amount")}:{" "}
              <strong>
                {formatAmount(selectedRow.amount)} {selectedRow.currency}
              </strong>
            </div>
            {selectedRow.paymentStatus === "partial" && (
              <>
                <div style={{ marginBottom: 8, color: "#52c41a" }}>
                  {t("Already paid")}:{" "}
                  {formatAmount(selectedRow.paidAmount || 0)} {selectedRow.currency}
                </div>
                <div style={{ marginBottom: 16, color: "#ff4d4f" }}>
                  {t("Remaining")}:{" "}
                  {formatAmount(selectedRow.remainingAmount || 0)} {selectedRow.currency}
                </div>
              </>
            )}
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>{t("Enter paid amount")}:</div>
              <InputNumber
                style={{ width: "100%" }}
                value={partialAmount}
                onChange={(value) => setPartialAmount(value || 0)}
                min={0.01}
                max={selectedRow.remainingAmount || selectedRow.amount}
                step={1}
                addonAfter={selectedRow.currency}
              />
            </div>
            <div style={{ marginTop: 8, color: "#8c8c8c", fontSize: 12 }}>
              {t("Remaining")}:{" "}
              {formatAmount(
                (selectedRow.remainingAmount || selectedRow.amount) - partialAmount,
              )}{" "}
              {selectedRow.currency}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};
